import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, Building2, CheckCircle, Calendar, Star, Check } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
export function MembershipPage() {
  const yearlyBenefits = ['Full access to all KMA UK events and seminars', 'Professional networking opportunities', 'Voting rights in association elections', 'Participation in charitable initiatives', 'Exclusive member newsletter and updates', 'Discounted rates for annual gala'];
  const lifetimeBenefits = ['All benefits of Yearly Membership', 'Lifetime access with one-off payment', 'Legacy member status recognition', 'Priority registration for high-demand events', 'No annual renewal required', 'Permanent voting rights'];
  return <>
      <SEO title="Membership" description="Join KMA UK and become part of a distinguished network of medical professionals" data-id="element-490" />

      <div className="bg-[#1e5f4f] text-white py-12 md:py-16" data-id="element-491">
        <div className="container mx-auto px-4 text-center" data-id="element-492">
          <FadeIn data-id="element-493">
            <Users className="h-12 w-12 mx-auto mb-4 text-white" data-id="element-494" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-3 text-white" data-id="element-495">
              Join KMA UK
            </h1>
            <p className="text-xl text-white max-w-2xl mx-auto leading-relaxed" data-id="element-496">
              Become part of a distinguished network of medical professionals
              committed to excellence, community, and humanitarian service.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Membership Tiers */}
      <Section data-id="element-497">
        <div className="max-w-6xl mx-auto" data-id="element-498">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-499">
            {/* Yearly Membership */}
            <FadeIn delay={0} data-id="element-500">
              <Card className="border-2 border-primary-200 shadow-xl relative overflow-hidden h-full flex flex-col" data-id="element-501">
                <CardHeader className="text-center pb-4 pt-6" data-id="element-502">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-3 mx-auto" data-id="element-503">
                    <Calendar className="h-8 w-8" data-id="element-504" />
                  </div>
                  <CardTitle className="text-3xl mb-2" data-id="element-505">
                    KMA UK Yearly Membership
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-506">
                    Annual commitment to our mission
                  </CardDescription>
                  <div className="mt-4" data-id="element-507">
                    <div className="text-5xl font-bold text-gray-900" data-id="element-508">£30</div>
                    <div className="text-gray-500 mt-1" data-id="element-509">per year</div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col pb-6" data-id="element-510">
                  <div className="flex-1" data-id="element-511">
                    <h4 className="font-bold text-gray-900 mb-3" data-id="element-512">
                      Benefits Include:
                    </h4>
                    <ul className="space-y-2" data-id="element-513">
                      {yearlyBenefits.map((benefit, i) => <li key={i} className="flex items-start text-sm" data-id="element-514">
                          <Check className="h-5 w-5 text-green-600 shrink-0 mt-0.5 mr-2" data-id="element-515" />
                          <span className="text-gray-700" data-id="element-516">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>

            {/* Lifetime Membership */}
            <FadeIn delay={0.1} data-id="element-517">
              <Card className="border-2 border-accent-200 shadow-xl h-full flex flex-col relative" data-id="element-518">
                <div className="absolute top-0 right-0 bg-accent-600 text-white px-4 py-1 text-sm font-bold" data-id="element-519">
                  Best Value
                </div>
                <CardHeader className="text-center pb-4 pt-6" data-id="element-520">
                  <div className="h-16 w-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-3 mx-auto" data-id="element-521">
                    <Star className="h-8 w-8" data-id="element-522" />
                  </div>
                  <CardTitle className="text-3xl mb-2" data-id="element-523">
                    KMA UK Lifetime Membership
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-524">
                    Permanent commitment to our cause
                  </CardDescription>
                  <div className="mt-4" data-id="element-525">
                    <div className="text-5xl font-bold text-gray-900" data-id="element-526">£300</div>
                    <div className="text-gray-500 mt-1" data-id="element-527">one-off payment</div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col pb-6" data-id="element-528">
                  <div className="flex-1" data-id="element-529">
                    <h4 className="font-bold text-gray-900 mb-3" data-id="element-530">
                      Benefits Include:
                    </h4>
                    <ul className="space-y-2" data-id="element-531">
                      {lifetimeBenefits.map((benefit, i) => <li key={i} className="flex items-start text-sm" data-id="element-532">
                          <Check className="h-5 w-5 text-green-600 shrink-0 mt-0.5 mr-2" data-id="element-533" />
                          <span className="text-gray-700" data-id="element-534">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>
          </div>

          <div className="text-center text-gray-500 text-sm italic" data-id="element-535">
            Please note: Membership fees are non-refundable.
          </div>
        </div>
      </Section>

      {/* Membership Payments */}
      <Section background="gray" data-id="element-536">
        <div className="max-w-3xl mx-auto" data-id="element-537">
          <FadeIn data-id="element-538">
            <Card className="border-2 border-primary-200 shadow-lg" data-id="element-539">
              <CardContent className="p-6 md:p-8" data-id="element-540">
                <div className="flex items-start gap-4 mb-5" data-id="element-541">
                  <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0" data-id="element-542">
                    <Building2 className="h-6 w-6" data-id="element-543" />
                  </div>
                  <div data-id="element-544">
                    <h2 className="text-2xl md:text-3xl font-serif font-bold text-primary-900 mb-2" data-id="element-545">
                      Membership Payments
                    </h2>
                    <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-546">
                      Please set up a standing order for your yearly membership
                      or make a one-off payment for lifetime membership. Once
                      completed, email us to confirm your full name and
                      membership type.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-5 md:p-6 border border-gray-200" data-id="element-547">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3" data-id="element-548">
                    Bank Details
                  </h3>
                  <div className="space-y-2 text-base md:text-lg" data-id="element-549">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200" data-id="element-550">
                      <span className="text-gray-600 font-medium" data-id="element-551">Bank</span>
                      <span className="text-gray-900 font-semibold" data-id="element-552">
                        Royal Bank of Scotland
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200" data-id="element-553">
                      <span className="text-gray-600 font-medium" data-id="element-554">
                        Sort Code
                      </span>
                      <span className="text-gray-900 font-semibold" data-id="element-555">
                        83 46 00
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200" data-id="element-556">
                      <span className="text-gray-600 font-medium" data-id="element-557">
                        Account Number
                      </span>
                      <span className="text-gray-900 font-semibold" data-id="element-558">
                        00251460
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2" data-id="element-559">
                      <span className="text-gray-600 font-medium" data-id="element-560">
                        Reference
                      </span>
                      <span className="text-gray-900 font-semibold" data-id="element-561">
                        Your Full Name
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </Section>

      {/* Full Membership */}
      <Section data-id="element-562">
        <div className="max-w-4xl mx-auto" data-id="element-563">
          <FadeIn data-id="element-564">
            <h2 className="text-3xl font-serif font-bold text-primary-900 mb-3 text-left" data-id="element-565">
              Full Membership
            </h2>
            <p className="text-lg text-gray-700 mb-4 text-left leading-relaxed" data-id="element-566">
              The following are eligible for full membership and are entitled to
              voting rights:
            </p>

            <div className="space-y-3" data-id="element-567">
              <div className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm" data-id="element-568">
                <div className="h-8 w-8 rounded-full bg-primary-600 text-white flex items-center justify-center shrink-0 font-bold" data-id="element-569">
                  1
                </div>
                <p className="text-gray-700 leading-relaxed pt-1" data-id="element-570">
                  Graduates from any recognised medical institution in Khyber
                  Pakhtunkhwa and work in UK, Ireland and Europe
                </p>
              </div>

              <div className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm" data-id="element-571">
                <div className="h-8 w-8 rounded-full bg-primary-600 text-white flex items-center justify-center shrink-0 font-bold" data-id="element-572">
                  2
                </div>
                <p className="text-gray-700 leading-relaxed pt-1" data-id="element-573">
                  Medical graduates born in the UK or Europe (EE or EEA) with
                  roots or established connections in Khyber Pakhtunkhwa,
                  currently working in the UK, Ireland, or Europe
                </p>
              </div>

              <div className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm" data-id="element-574">
                <div className="h-8 w-8 rounded-full bg-primary-600 text-white flex items-center justify-center shrink-0 font-bold" data-id="element-575">
                  3
                </div>
                <p className="text-gray-700 leading-relaxed pt-1" data-id="element-576">
                  Medical graduates from other parts of Pakistan or anywhere in
                  the world with roots or established connections in Khyber
                  Pakhtunkhwa
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Associate Membership */}
      <Section background="gray" data-id="element-577">
        <div className="max-w-4xl mx-auto" data-id="element-578">
          <FadeIn data-id="element-579">
            <h2 className="text-3xl font-serif font-bold text-primary-900 mb-3 text-left" data-id="element-580">
              Associate Membership
            </h2>
            <p className="text-lg text-gray-700 mb-4 text-left leading-relaxed" data-id="element-581">
              This is offered to:
            </p>

            <div className="space-y-3" data-id="element-582">
              <div className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-583">
                <CheckCircle className="h-6 w-6 text-primary-600 shrink-0 mt-0.5" data-id="element-584" />
                <p className="text-gray-700 leading-relaxed" data-id="element-585">
                  Non doctor medical professionals such as nurses, pharmacists,
                  and allied health professionals
                </p>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-586">
                <CheckCircle className="h-6 w-6 text-primary-600 shrink-0 mt-0.5" data-id="element-587" />
                <p className="text-gray-700 leading-relaxed" data-id="element-588">
                  Non medical professionals who actively support and contribute
                  to KMA activities and objectives
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Benefits */}
      <Section data-id="element-589">
        <div className="max-w-4xl mx-auto" data-id="element-590">
          <FadeIn data-id="element-591">
            <h2 className="text-3xl font-serif font-bold text-primary-900 mb-5 text-left" data-id="element-592">
              Benefits of KMA UK Membership
            </h2>

            <div className="space-y-3" data-id="element-593">
              {['Connect with fellow doctors and medical professionals from Khyber Pakhtunkhwa living or working in the UK and worldwide', 'Support for UK born graduates and second generation professionals', 'Collaboration with KP medical institutions', 'Contribution to healthcare and medical education through teaching and advisory roles', 'Support charitable causes in the UK and Pakistan', 'Mentorship and guidance for newly arriving doctors in the UK and Ireland'].map((benefit, i) => <div key={i} className="flex items-start gap-4 bg-gray-50 p-4 md:p-5 rounded-lg shadow-sm" data-id="element-594">
                  <div className="h-8 w-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center shrink-0 font-bold text-lg" data-id="element-595">
                    {i + 1}
                  </div>
                  <p className="text-gray-700 leading-relaxed pt-1 text-base md:text-lg" data-id="element-596">
                    {benefit}
                  </p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* CTA */}
      <Section background="gray" className="text-center" data-id="element-597">
        <FadeIn data-id="element-598">
          <div className="max-w-2xl mx-auto" data-id="element-599">
            <Mail className="h-12 w-12 mx-auto mb-3 text-primary-600" data-id="element-600" />
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-3" data-id="element-601">
              Ready to Join?
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-602">
              After setting up your standing order or making your payment,
              please email us to confirm your membership details.
            </p>
            <Link to="/contact" data-id="element-603">
              <Button size="lg" variant="primary" data-id="element-604">
                <Mail className="h-5 w-5 mr-2" data-id="element-605" />
                Email Confirmation
              </Button>
            </Link>
          </div>
        </FadeIn>
      </Section>
    </>;
}